/**
 * Groups a draft's flat, ordered event list for the Events table so that related events can
 * only be moved together:
 *   - a combined parent and its children form one unit (children only reorder inside it);
 *   - units tied by brother_id form one group (regular split A+B, combined split A+B, and a
 *     conjunction pair with its final) and are moved as a whole.
 * Units inside a group keep a fixed order (by id: the original/A row is always the lowest id).
 *
 * @param {Array} events flat list, already in display order
 * @returns {Array<{ id: number, units: Array<{ row: Object, children: Object[] }> }>}
 */
export function buildEventGroups(events) {
  const position = new Map(events.map((event, index) => [event.id, index]))
  const ids = new Set(events.map(event => event.id))

  const childrenByParent = new Map()
  const roots = []

  events.forEach((event) => {
    if (event.parent_id != null && ids.has(event.parent_id)) {
      if (!childrenByParent.has(event.parent_id)) childrenByParent.set(event.parent_id, [])
      childrenByParent.get(event.parent_id).push(event)
    } else {
      roots.push(event)
    }
  })

  const rootById = new Map(roots.map(root => [root.id, root]))
  const family = new Map(roots.map(root => [root.id, root.id]))

  function find(id) {
    while (family.get(id) !== id) id = family.get(id)
    return id
  }

  roots.forEach((root) => {
    if (root.brother_id != null && rootById.has(root.brother_id)) {
      family.set(find(root.id), find(root.brother_id))
    }
  })

  const unitsByFamily = new Map()
  roots.forEach((root) => {
    const key = find(root.id)
    if (!unitsByFamily.has(key)) unitsByFamily.set(key, [])
    unitsByFamily.get(key).push({ row: root, children: childrenByParent.get(root.id) || [] })
  })

  return [...unitsByFamily.values()]
    .map((units) => {
      units.sort((a, b) => a.row.id - b.row.id)
      return { id: units[0].row.id, units, position: Math.min(...units.map(unit => position.get(unit.row.id))) }
    })
    .sort((a, b) => a.position - b.position)
    .map(({ id, units }) => ({ id, units }))
}

export function flattenEventGroups(groups) {
  return groups.flatMap(group => group.units.flatMap(unit => [unit.row.id, ...unit.children.map(child => child.id)]))
}

/**
 * Same grouping for a list that has no parent rows (only leaf events): a unit is the set of
 * rows sharing a parent_id (or a lone event), and units are tied into one group through
 * brother_id (own, or the parent's `parent_brother_id`) — a regular split pair, a combined
 * split A+B, or a conjunction pair with its final.
 *
 * @param {Array} events flat list, already in display order
 * @returns {Array<{ id: number, rows: Object[] }>} rows are ordered unit by unit (lowest id first)
 */
export function buildLeafEventGroups(events) {
  const unitKey = event => (event.parent_id != null ? event.parent_id : event.id)
  const brotherKey = event => (event.parent_id != null ? event.parent_brother_id : event.brother_id)

  const family = new Map(events.map(event => [unitKey(event), unitKey(event)]))

  function find(key) {
    while (family.get(key) !== key) key = family.get(key)
    return key
  }

  events.forEach((event) => {
    const brother = brotherKey(event)
    if (brother != null && family.has(brother)) {
      family.set(find(unitKey(event)), find(brother))
    }
  })

  const groups = new Map()
  events.forEach((event, index) => {
    const key = find(unitKey(event))
    if (!groups.has(key)) groups.set(key, { rows: [], position: index })
    groups.get(key).rows.push({ event, index })
  })

  return [...groups.values()]
    .sort((a, b) => a.position - b.position)
    .map(({ rows }) => {
      rows.sort((a, b) => unitKey(a.event) - unitKey(b.event) || a.index - b.index)
      return { id: rows[0].event.id, rows: rows.map(row => row.event) }
    })
}



// WEBPACK FOOTER //
// ./src/utils/eventGroups.js